import { Injectable } from '@angular/core';
import { tap, mapTo, switchMap, finalize } from 'rxjs/operators';
import { ApiService } from '../shared/services/core/api.service';
import { of, Observable } from 'rxjs';
import { ChildStore } from './child.store';
import { ChildQuery } from './child.query';
import { ChildViewModel } from './models/child-view.model';
import { MemberQuery } from '../account/member/member.query';
import { LoggingService } from '../shared/services/core/logging.service';
import { ErrorStateService } from '../shared/services/core/error.service';
import { Guardian } from './models/guardian.model';
import { ChildCheckInAttendance } from './models/child-check-in-attendance.model';
import { ChildAttendanceModel } from './models/child-attendance.model';
import { ChildModel } from './models/child.model';
import { withTransaction } from '@datorama/akita';

@Injectable({ providedIn: 'root' })
export class ChildService {

  constructor(
    private childStore: ChildStore,
    private childQuery: ChildQuery,
    private api: ApiService,
    private logService: LoggingService,
    private errorService: ErrorStateService,
    private memberQuery: MemberQuery
  ) {}

  /** Fetches the children and puts them in the store. */
  fetchChildren(): Observable<null> {
    this.childStore.setLoading(true);

    const request$: Observable<null> = this.api.get<ChildViewModel>('guardian/children').pipe(
      withTransaction((children) => {
        this.childStore.set(children);
        this.childStore.update({ loaded: true });
      }),
      mapTo(null),
      finalize(() => this.childStore.setLoading(false))
    );

    return this.childQuery.getHasCache() ? of(null) : request$;
  }

  /** Fetch the current member's guardian information from the API and update the store's state. */
  fetchGuardian(): Observable<null> {
    return this.api.getAnySingle<Guardian>('guardian').pipe(
      tap((guardian) => this.childStore.update({ guardian })),
      mapTo(null)
    );
  }

  /** Fetch child details using child member id*/
  fetchChildByChildMemberId(childMemberId: string): Observable<ChildModel> {
    return this.api.getAnySingle<ChildModel>(`guardian/childdetails/${childMemberId}`);
  }

  /** Fetch the max amount of children a guardian can add to their profile. */
  fetchChildCountLimit(): Observable<null> {
    return this.api.getAnySingle<number>('guardian/childcountlimit').pipe(
      tap((limit) => this.childStore.update({ childLimit: limit })),
      mapTo(null)
    );
  }

  /** Make a request to register the current account as a guardian. */
  registerAsGuardian(): Observable<null> {
    const memberId = this.memberQuery.getUserContext().memberId;
    if (!memberId) {
      this.logService.error('Member ID could not be found for registering as a guardian.');
      this.errorService.addError('Something went wrong, please try again.');
      return of(null);
    }

    let body = JSON.stringify(memberId);
    return this.api.post('guardian/register', body).pipe(switchMap(() => this.fetchGuardian()));
  }

  /** Make a request to print the child card with the new barcode */
  lostCard(childId: number, barcode: string) {
    return this.api.post(`guardian/lostcard/${childId}/${barcode}`, null).toPromise();
  }

  /**
   * Make a request to print the child card with the new barcode
   * @deprecated use the standard lostCard which returns as a promise
   */
  lostCardObservable(childId: number, barcode: string) {
    return this.api.post(`guardian/lostcard/${childId}/${barcode}`, null);
  }

  /** Make a request to get list of attendance  */
  attendance(): Observable<ChildAttendanceModel[]> {
    return this.api.get<ChildAttendanceModel>(`guardian/children/attendance`);
  }

  /** Make a request to get the chick-in Attendance */
  checkInAttendance(memberId: string = ''): Observable<ChildCheckInAttendance[]> {
    if (memberId === '') {
      memberId = this.memberQuery.getUserContext().memberId;
    }
    return this.api.get<ChildCheckInAttendance>(`guardian/children/checkinattendance/${memberId}`);
  }

  /** This endpoint is used to return create a guardian link request */
  linkRequest(memberId: string, lastName: string, dateOfBirth: Date | string, externalRefNumber: string = '') {
    const request = encodeURI(`memberId=` + memberId + '&lastName=' + lastName + '&dateOfBirth=' + dateOfBirth + '&externalRefNumber=' + externalRefNumber);
    return this.api.post(`guardian/linkrequest?` + request, null).toPromise();
  }

  /** This endpoint is used to complete the link request */
  processLinkRequest(linkCode: string) {
    return this.api.post(`guardian/linkrequest/${linkCode}`, null).toPromise();
  }

  /** This endpoint is used to delete guardian link request */
  deleteLinkRequest(memberId: string) {
    return this.api.delete(`guardian/linkrequest/`, memberId).toPromise();
  }

  /** This endpoint is used to return a list of children, connected and pending child   */
  getLinkRequests(): Observable<ChildModel[]> {
    return this.api.get<ChildModel>(`guardian/linkrequests`);
  }

  /**
   * This will disable cache of the child store, so
   * that a fetch can be made again.
   */
  disableChildStoreCache() {
    this.childStore.setHasCache(false);
  }
}
